import { useRef } from "react";
import { Paperclip, X, FileText, Image as ImageIcon, UploadCloud } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_FILES = 3;
const MAX_BYTES = 1024 * 1024;

export function AttachmentsField({ value = [], onChange, disabled = false, label = "Attachments", compact = false }) {
  const fileInput = useRef(null);

  function validateFile(f) {
    const name = (f.name || "").toLowerCase();
    const ext = name.slice(name.lastIndexOf("."));
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`${f.name}: only PDF, JPG, PNG allowed`);
      return false;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name}: ${(f.size / 1024).toFixed(0)} KB exceeds 1 MB limit`);
      return false;
    }
    return true;
  }

  async function upload(files) {
    if (!files || !files.length) return;
    const current = value || [];
    const remaining = MAX_FILES - current.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} attachments allowed`);
      return;
    }
    const toUpload = Array.from(files).slice(0, remaining).filter(validateFile);
    if (files.length > remaining) {
      toast.warning(`Only ${remaining} more file(s) allowed — extras skipped`);
    }
    if (!toUpload.length) return;
    const uploaded = [];
    for (const f of toUpload) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/vendor-attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} attachment(s) added`);
    }
  }

  function remove(idx) {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  }

  const count = (value || []).length;
  const full = count >= MAX_FILES;

  return (
    <div data-testid="attachments-field" className={compact ? "" : "space-y-2"}>
      {!compact && (
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
            <Paperclip size={12}/> {label} <span className="text-slate-400 normal-case font-normal tracking-normal">(PDF / JPG / PNG · max 3 files · 1 MB each)</span>
          </div>
          <div className="text-[10px] text-slate-500 tabular">{count}/{MAX_FILES}</div>
        </div>
      )}

      <input ref={fileInput} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        className="hidden" disabled={disabled || full}
        onChange={e => { upload(e.target.files); e.target.value = ""; }}
        data-testid="attachments-file-input"/>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {(value || []).map((a, i) => (
          <AttachmentChip key={a.id || a.stored_as || i} a={a}
            onRemove={disabled ? null : () => remove(i)}
            testid={`attachment-chip-${i}`}/>
        ))}
        {!full && !disabled && (
          <button type="button" onClick={() => fileInput.current?.click()}
            data-testid="attachments-add-btn"
            className="border-2 border-dashed border-slate-300 hover:border-[#F97316] hover:bg-orange-50 text-slate-500 hover:text-[#F97316] px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors">
            <UploadCloud size={14}/> Add file
          </button>
        )}
      </div>
    </div>
  );
}

export function AttachmentChip({ a, onRemove, testid }) {
  const name = a.filename || a.stored_as || "attachment";
  const lower = name.toLowerCase();
  const isImg = /\.(jpe?g|png)$/i.test(lower);
  const url = a.url ? buildHref(a.url) : "#";
  return (
    <div data-testid={testid} className="border border-slate-200 bg-white px-2 py-1.5 flex items-center gap-2 group">
      <div className="text-slate-500">
        {isImg ? <ImageIcon size={14}/> : <FileText size={14}/>}
      </div>
      <a href={url} target="_blank" rel="noreferrer"
        className="text-[11px] text-slate-700 hover:text-[#F97316] truncate flex-1" title={name}>
        {name}
      </a>
      <span className="text-[10px] text-slate-400 tabular flex-shrink-0">{prettySize(a.size)}</span>
      {onRemove && (
        <button type="button" onClick={onRemove}
          data-testid={`${testid}-remove`}
          className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity">
          <X size={12}/>
        </button>
      )}
    </div>
  );
}

function buildHref(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

function prettySize(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default AttachmentsField;
